import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Button, Form, Spinner, Container, Row, Col, Badge, Alert } from "react-bootstrap";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import UserContext from "../../context/UserContext";

const PaymentApprovalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: contextUser, setUser } = useContext(UserContext);
  
  const [request, setRequest] = useState(null);
  const [comment, setComment] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]); // Đảm bảo trạng thái luôn lưu trữ một Mảng (Array)
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAttachmentRequired, setIsAttachmentRequired] = useState(false);

  useEffect(() => {
    fetchPaymentDetail();
  }, [id, contextUser]);

  const handleLogout = () => {
    localStorage.clear();
    if (typeof setUser === "function") setUser(null);
    navigate("/login", { replace: true });
  };

  const getDownloadUrl = (path) => {
    if (!path) return "#";
    const baseUrl = API_ENDPOINTS.url.replace(/\/$/, ""); 
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    
    return baseUrl.includes("localhost") 
      ? `${baseUrl}${cleanPath}` 
      : `${baseUrl}/api${cleanPath}`;
  };

  const handleDownload = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      window.open(url, '_blank');
    }
  };

  const handleViewFile = async (url, fileName) => {
    if (!url || url === "#") return;

    const fileExtension = fileName.split('.').pop().toLowerCase();
    const officeExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];

    if (officeExtensions.includes(fileExtension)) {
      handleDownload(url, fileName);
      return;
    }

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      
      let mimeType = blob.type;
      if (fileExtension === 'pdf') mimeType = 'application/pdf';
      
      const viewBlob = new Blob([blob], { type: mimeType });
      const blobUrl = window.URL.createObjectURL(viewBlob);
      
      const newTab = window.open("", "_blank");
      if (!newTab) return;

      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(fileExtension);

      newTab.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${fileName}</title>
          <style>
            body, html { margin: 0; padding: 0; width: 100%; height: 100%; overflow: hidden; background-color: #525659; }
            iframe, embed { width: 100%; height: 100%; border: none; }
            .img-container { width: 100%; height: 100%; display: flex; justify-content: center; align-items: center; background-color: #0e0e0e; overflow: auto; }
            img { max-width: 100%; max-height: 100%; object-fit: contain; }
          </style>
        </head>
        <body>
          ${isImage ? `
            <div class="img-container">
              <img src="${blobUrl}" alt="${fileName}" />
            </div>
          ` : `
            <embed src="${blobUrl}" type="${mimeType}"></embed>
          `}
        </body>
        </html>
      `);
      newTab.document.close();
      
    } catch (error) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // --- HÀM XỬ LÝ SỰ KIỆN PASTE ẢNH TỪ CLIPBOARD (MỚI & SỬA ĐỒNG BỘ) ---
  const handlePaste = (e) => {
    const items = (e.clipboardData || e.originalEvent.clipboardData).items;
    const newFiles = [];

    for (let i = 0; i < items.length; i++) {
      // Nhận diện dữ liệu dạng hình ảnh
      if (items[i].type.indexOf("image") !== -1) {
        const blob = items[i].getAsFile();
        if (blob) {
          // Tạo một thực thể File mới từ Blob với tên ngẫu nhiên tránh trùng lặp
          const fileFromBlob = new File(
            [blob], 
            `pasted_img_${Date.now()}_${i}.png`, 
            { type: blob.type }
          );
          newFiles.push(fileFromBlob);
        }
      }
    }

    if (newFiles.length > 0) {
      // Đẩy ảnh mới gộp chung vào mảng selectedFiles hiện tại
      setSelectedFiles((prevFiles) => {
        const currentList = prevFiles ? Array.from(prevFiles) : [];
        return [...currentList, ...newFiles];
      });
    }
  };

  const fetchPaymentDetail = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(`${API_ENDPOINTS.getPaymentRequestById(id)}`);
      const data = res.data;
      setRequest(data);

      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const currentLoginId = Number(contextUser?.id || contextUser?.userId || storedUser.id || storedUser.userId);
      
      const currentStepLog = data.approvalLogs?.find(
        (log) => Number(log.stepOrder) === Number(data.currentStep) && !log.isProcessed
      );

      const hasPermission = currentStepLog && Number(currentLoginId) === Number(currentStepLog.approverId);
      setIsAuthorized(hasPermission);

      if (hasPermission && data.currentStep) {
        try {
          const configRes = await axiosClient.get(API_ENDPOINTS.checkAttachmentRequirement(data.currentStep));
          setIsAttachmentRequired(configRes.data?.isRequired || false);
        } catch (e) {
          console.error("Error checking attachment requirement:", e);
        }
      }
    } catch (err) {
      console.error("Error fetching detail:", err);
      alert(err.response?.data?.message || "An error occurred while loading data. Redirecting to login...");
      localStorage.clear();
      if (typeof setUser === "function") {
        setUser(null);
      }
      navigate("/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const getPaymentTargetLabel = (target) => {
    const targets = {
      0: "Supplier (Nhà cung cấp)",
      1: "Customer (Khách hàng)",
      2: "Internal (Nội bộ)"
    };
    return targets[target] ?? "Unknown";
  };

  const handleAction = async (isApprove) => {
    if (isApprove && isAttachmentRequired && (!selectedFiles || selectedFiles.length === 0)) {
      alert("This step requires an attachment. Please select files before approving.");
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);
    
    const action = isApprove ? "approve" : "reject";
    const formData = new FormData();
    formData.append("comment", comment.trim() || (isApprove ? "Approved" : "Rejected"));
    
    if (isApprove && selectedFiles && selectedFiles.length > 0) {
      // Chạy vòng lặp duyệt qua mảng Array chuẩn hóa để đính vào Request gửi đi
      for (let i = 0; i < selectedFiles.length; i++) {
        formData.append("files", selectedFiles[i]);
      }
    }

    try {
      await axiosClient.post(
        isApprove ? API_ENDPOINTS.approvePaymentRequest(id) : API_ENDPOINTS.rejectPaymentRequest(id), 
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      
      alert(`Payment request ${action}ed successfully!`);
      navigate("/payment-approval");
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "An error occurred. Redirecting to login...");
      
      localStorage.clear();
      if (typeof setUser === "function") setUser(null);
      navigate("/login", { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || !request) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;

  const ReadOnlyField = ({ label, vn, value, className = "" }) => (
    <div className={`mb-3 ${className}`}>
      <label className="form-label font-weight-bold small text-muted d-block">{label} <span className="text-muted font-weight-normal">({vn})</span></label>
      <div className="form-control form-control-sm bg-light border-0 font-weight-bold">{value || "-"}</div>
    </div>
  );

  return (
    <Container className="py-4">
      <Card className="shadow-lg border-0 rounded-4 mx-auto" style={{ maxWidth: "1000px" }}>
        <Card.Header className="bg-primary text-white py-3 px-4 d-flex justify-content-between align-items-center">
          <h5 className="mb-0"><i className="fas fa-file-invoice-dollar mr-2"></i> Payment Request Details</h5>
          <Badge bg={request.status === 2 ? "success" : request.status === 3 ? "danger" : "warning"} className="p-2">
            {request.status === 1 ? "PENDING" : request.status === 2 ? "APPROVED" : "REJECTED"}
          </Badge>
        </Card.Header>

        <Card.Body className="p-4">
          <h6 className="text-primary font-weight-bold text-uppercase border-bottom pb-2 mb-3">General Information</h6>
          <Row>
            <Col md={12}><ReadOnlyField label="Title" vn="Tiêu đề" value={request.title} /></Col>
            <Col md={4}><ReadOnlyField label="Proposal Date" vn="Ngày đề xuất" value={new Date(request.requestDate).toLocaleDateString()} /></Col>
            <Col md={4}><ReadOnlyField label="Due Date" vn="Ngày đến hạn" value={new Date(request.dueDate).toLocaleDateString()} /></Col>
            <Col md={4}><ReadOnlyField label="Department" vn="Bộ phận" value={request.department?.name || "-"} /></Col>
            <Col md={6}><ReadOnlyField label="Business Unit" vn="Đơn vị kinh doanh" value={request.businessUnit} /></Col>
          </Row>

          <h6 className="text-primary font-weight-bold text-uppercase border-bottom pb-2 mb-3 mt-3">Context & Reason</h6>
          <Row>
            <Col md={4}><ReadOnlyField label="Cost Ownership" vn="Chi phí trực thuộc" value={request.costOwnership} /></Col>
            <Col md={4}><ReadOnlyField label="Cost Type" vn="Loại chi phí" value={request.costType} /></Col>
            <Col md={4}><ReadOnlyField label="Document Type" vn="Loại chứng từ" value={request.documentType} /></Col>
            <Col md={6}><ReadOnlyField label="Project Name" vn="Tên dự án" value={request.project?.name || "-"} /></Col>
            <Col md={6}><ReadOnlyField label="Purchase Order No" vn="Mã đơn hàng" value={request.orderCode} /></Col>
          </Row>
          <ReadOnlyField label="Payment Reason Details" vn="Chi tiết lý do" value={request.paymentReasonDetails} />

          <h6 className="text-primary font-weight-bold text-uppercase border-bottom pb-2 mb-3 mt-3">Payment & Beneficiary</h6>
          <Row>
            <Col md={6}>
              <ReadOnlyField 
                label="Payment Target" 
                vn="Đối tượng thanh toán" 
                value={getPaymentTargetLabel(request.paymentTarget)} 
              />
            </Col>
            <Col md={6}><ReadOnlyField label="Company Name" vn="Tên công ty" value={request.supplier?.name} /></Col>
            <Col md={6}><ReadOnlyField label="Beneficiary Name" vn="Tên người thụ hưởng" value={request.beneficiaryName} /></Col>
            <Col md={6}><ReadOnlyField label="Bank Account" vn="Số tài khoản" value={request.bankAccountNumber} /></Col>
            <Col md={6}><ReadOnlyField label="Bank Name" vn="Tên ngân hàng" value={request.bankName} /></Col>
            <Col md={6}><ReadOnlyField label="Total Amount" vn="Tổng tiền" value={`${request.totalAmount?.toLocaleString()} ${request.currency}`} /></Col>
            <Col md={12}><ReadOnlyField label="Transfer Content" vn="Nội dung chuyển khoản" value={request.transferContent} /></Col>
            <Col md={6}><ReadOnlyField label="Internal Doc Type" vn="Loại chứng từ nội bộ" value={request.internalDocumentType} /></Col>
          </Row>

          <h6 className="text-primary font-weight-bold text-uppercase border-bottom pb-2 mb-3 mt-3">Attachments</h6>
          <div className="row mb-3">
            <div className="col-md-6 mb-3">
              <label className="form-label font-weight-bold small text-muted">Invoice Files</label>
              <div>
                {request.paymentRequestFiles && request.paymentRequestFiles.filter(f => f.fileType === 0).length > 0 ? (
                  request.paymentRequestFiles
                    .filter(f => f.fileType === 0)
                    .map((file, idx) => {
                      const fileUrl = getDownloadUrl(file.fileUrl);
                      return (
                        <div key={file.id || idx} className="d-flex align-items-center justify-content-between bg-light p-2 rounded my-1 border">
                          <span className="small text-truncate max-w-50 fw-bold text-dark">
                            <i className="fas fa-file-invoice mr-2 text-secondary"></i>
                            {file.fileName}
                          </span>
                          <div className="d-flex gap-2">
                            <Button
                              type="button"
                              variant="outline-primary"
                              size="sm"
                              className="py-0 px-2 btn-sm"
                              style={{ fontSize: "12px" }}
                              onClick={() => handleViewFile(fileUrl, file.fileName || "Document.pdf")}
                            >
                              <i className="fas fa-eye me-1"></i>View
                            </Button>
                            <button
                              type="button"
                              onClick={() => handleDownload(fileUrl, file.fileName || `Invoice_${idx + 1}.pdf`)}
                              className="btn btn-sm btn-link p-0 text-primary small"
                              style={{ textDecoration: 'underline', border: 'none', background: 'none' }}
                            >
                              <i className="fas fa-file-download mr-1"></i>Download
                            </button>
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <span className="text-muted small d-block p-1">No invoices attached</span>
                )}
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label font-weight-bold small text-muted">Statement Files</label>
              <div>
                {request.paymentRequestFiles && request.paymentRequestFiles.filter(f => f.fileType === 1).length > 0 ? (
                  request.paymentRequestFiles
                    .filter(f => f.fileType === 1)
                    .map((file, idx) => {
                      const fileUrl = getDownloadUrl(file.fileUrl);
                      return (
                        <div key={file.id || idx} className="d-flex align-items-center justify-content-between bg-light p-2 rounded my-1 border">
                          <span className="small text-truncate max-w-50 fw-bold text-dark">
                            <i className="fas fa-file-alt mr-2 text-secondary"></i>
                            {file.fileName}
                          </span>
                          <div className="d-flex gap-2">
                            <Button
                              type="button"
                              variant="outline-primary"
                              size="sm"
                              className="py-0 px-2 btn-sm"
                              style={{ fontSize: "12px" }}
                              onClick={() => handleViewFile(fileUrl, file.fileName || "Document.pdf")}
                            >
                              <i className="fas fa-eye me-1"></i>View
                            </Button>
                            <button
                              type="button"
                              onClick={() => handleDownload(fileUrl, file.fileName || `Statement_${idx + 1}.pdf`)}
                              className="btn btn-sm btn-link p-0 text-primary small"
                              style={{ textDecoration: 'underline', border: 'none', background: 'none' }}
                            >
                              <i className="fas fa-file-download mr-1"></i>Download
                            </button>
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <span className="text-muted small d-block p-1">No statements attached</span>
                )}
              </div>
            </div>
          </div>

          {/* --- KÌM CỦA KHU VỰC PHÊ DUYỆT (ĐÃ SỬA ĐỂ HỖ TRỢ COPPY PASTE) --- */}
          {isAuthorized && (
            <div className="mt-4 p-3 border rounded bg-light">
              <Form.Group className="mb-3">
                <Form.Label className="font-weight-bold">
                  Approver Comment <span className="text-muted font-weight-normal">(Có thể click vào đây nhấn Ctrl+V để dán hình ảnh)</span>
                </Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={2} 
                  value={comment} 
                  onChange={(e) => setComment(e.target.value)} 
                  onPaste={handlePaste} // Lắng nghe sự kiện paste ảnh
                  placeholder="Nhập nội dung bình luận hoặc nhấn Ctrl+V để đính kèm ảnh chụp màn hình nhanh..."
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="font-weight-bold">
                  Attach Documents {isAttachmentRequired && <span className="text-danger">*</span>}
                </Form.Label>
                {isAttachmentRequired && (!selectedFiles || selectedFiles.length === 0) && (
                  <Alert variant="danger" className="py-2 small">Mandatory file upload required for this step.</Alert>
                )}
                
                {/* SỬA ĐỒNG BỘ: Chuyển dữ liệu FileList sang Mảng (Array.from) */}
                <Form.Control 
                  type="file" 
                  multiple 
                  onChange={(e) => setSelectedFiles(e.target.files ? Array.from(e.target.files) : [])} 
                />

                {/* GIAO DIỆN HIỂN THỊ DANH SÁCH FILE (Bao gồm cả file tải lên thông thường và file dán ảnh) */}
                {selectedFiles && selectedFiles.length > 0 && (
                  <div className="mt-2">
                    <small className="text-muted d-block mb-1 font-weight-bold">Tệp danh sách đính kèm hiện tại ({selectedFiles.length}):</small>
                    <div className="d-flex flex-wrap gap-2">
                      {selectedFiles.map((file, index) => (
                        <Badge key={index} bg="dark" className="p-2 d-flex align-items-center text-white">
                          <i className={file.type.startsWith('image/') ? "fas fa-image me-1" : "fas fa-file me-1"}></i>
                          <span className="text-truncate" style={{ maxWidth: "200px" }}>{file.name}</span>
                          <span 
                            className="ms-2 fw-bold text-danger"
                            style={{ cursor: 'pointer', fontSize: '14px' }} 
                            onClick={() => {
                              const updated = selectedFiles.filter((_, i) => i !== index);
                              setSelectedFiles(updated);
                            }}
                          >
                            &times;
                          </span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </Form.Group>

              <div className="d-flex gap-2">
                <Button variant="danger" onClick={() => handleAction(false)} disabled={isSubmitting} className="d-flex align-items-center">
                  {isSubmitting && <Spinner as="span" animation="border" size="sm" className="me-2" role="status" aria-hidden="true" />}
                  REJECT
                </Button>
                <Button variant="success" onClick={() => handleAction(true)} disabled={isSubmitting} className="d-flex align-items-center">
                  {isSubmitting && <Spinner as="span" animation="border" size="sm" className="me-2" role="status" aria-hidden="true" />}
                  APPROVE
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PaymentApprovalDetail;